import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import '../styles/PendingApproval.css';

const PendingApproval = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUserStatus();
  }, []);

  const checkUserStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        navigate('/standouts/login');
        return;
      }

      // Get user approval status
      const { data: approvalData } = await supabase
        .rpc('check_user_approval', { p_user_id: user.id });

      if (approvalData && approvalData.length > 0) {
        const { approved, role, acknowledged } = approvalData[0];

        if (approved) {
          // Approved: show the one-time approved screen first, then submit.
          navigate(acknowledged ? '/standouts/submit' : '/standouts/account-approved');
          return;
        }

        setUserInfo({ email: user.email, role });
      }
    } catch (error) {
      console.error('Error checking user status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/standouts');
  };

  if (loading) {
    return (
      <div className="pending-approval-screen">
        <div className="pending-approval-container">
          <div className="pending-approval-card">
            {/* Icon Skeleton */}
            <div className="skeleton-icon"></div>

            {/* Title Skeleton */}
            <div className="skeleton-title"></div>

            {/* Message Skeleton */}
            <div className="skeleton-text"></div>
            <div className="skeleton-text skeleton-text-short"></div>

            {/* Info Box Skeleton */}
            <div className="skeleton-info-box">
              <div className="skeleton-info-row">
                <div className="skeleton-label"></div>
                <div className="skeleton-value"></div>
              </div>
              <div className="skeleton-info-row">
                <div className="skeleton-label"></div>
                <div className="skeleton-value"></div>
              </div>
            </div>

            {/* Steps Section Skeleton */}
            <div className="skeleton-steps">
              <div className="skeleton-steps-title"></div>
              <div className="skeleton-step-item"></div>
              <div className="skeleton-step-item"></div>
              <div className="skeleton-step-item"></div>
            </div>

            {/* Buttons Skeleton */}
            <div className="skeleton-buttons">
              <div className="skeleton-button"></div>
              <div className="skeleton-button"></div>
            </div>

            {/* Support Text Skeleton */}
            <div className="skeleton-support"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pending-approval-screen">
      <div className="pending-approval-container">
        <div className="pending-approval-card">
          <div className="pending-approval-icon">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>

          <h1 className="pending-approval-title">Account Pending Approval</h1>

          <p className="pending-approval-message">
            Thank you for signing up! Your account is currently under review by our admin team.
          </p>

          {userInfo && (
            <div className="pending-approval-info">
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">{userInfo.email}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Role:</span>
                <span className="info-value">
                  {userInfo.role === 'league_admin' ? 'League Admin' :
                   userInfo.role ? userInfo.role.charAt(0).toUpperCase() + userInfo.role.slice(1) : 'User'}
                </span>
              </div>
            </div>
          )}

          <div className="pending-approval-steps">
            <h3>What happens next?</h3>
            <ol>
              <li>Our admin team will review your account</li>
              <li>You'll receive an email notification once approved</li>
              <li>After approval, you can submit standouts for your organization</li>
            </ol>
          </div>

          <div className="pending-approval-actions">
            <button
              className="btn-secondary"
              onClick={() => navigate('/standouts')}
            >
              Back to Standouts
            </button>
            <button
              className="btn-text"
              onClick={handleSignOut}
            >
              Sign Out
            </button>
          </div>

          <div className="pending-approval-support">
            <p>Need help? Contact us at <a href="mailto:support@legacystat.com">support@legacystat.com</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingApproval;
